import { Service, type BackendError } from '@genial/services';
import type { BasketEnumsBasketOrderStatusEn, MetaResponse } from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { screenName } from '../../../pages/RedeemBatchTicket/utils/constants';
import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  normalizeBatchTicketRedeemOffers,
  normalizeBatchTicketRedeemRows,
  normalizeRedeemBatchTicketRequestsData,
} from '../normalize';

export const batchTicketRedeemBaseFileQuery = selector({
  key: 'batchTicketRedeemBaseFileQuery',
  get: async () => {
    try {
      const { data } = await Service.Basket.V1.getFile({
        screenName,
      });

      const normalizedData = normalizeRedeemBatchTicketRequestsData(data || []);

      return { data: normalizedData };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [] };
      }

      throw error;
    }
  },
});

type BatchTicketRedeemOffersQueryResponse = {
  data: ReturnType<typeof normalizeBatchTicketRedeemOffers>;
  meta: MetaResponse;
};

type BatchTicketRedeemOffersQueryParams = {
  page: number;
  basketAdvisorId: string;
  searchTerm: string;
  basketOfferStatus?: BasketEnumsBasketOrderStatusEn[];
  applicationMinimumAmount?: number;
  applicationMaximumAmount?: number;
};

export const batchTicketRedeemOffersQuery = selectorFamily<
  BatchTicketRedeemOffersQueryResponse,
  BatchTicketRedeemOffersQueryParams
>({
  key: 'batchTicketRedeemOffersQuery',
  get: ({
    page,
    basketAdvisorId,
    searchTerm,
    applicationMaximumAmount,
    applicationMinimumAmount,
    basketOfferStatus,
  }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const isFiltering = searchTerm.length > 0;

        const { meta, data } = await Service.Basket.V1.getCustomersByOffersBasketAdvisorFilter({
          basketAdvisorId,
          screenName: 'boleta-lote',
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          page,
          ...(isFiltering && { searchTerm }),
          applicationMaximumAmount,
          applicationMinimumAmount,
          basketOfferStatus,
        });

        const normalizedData = normalizeBatchTicketRedeemOffers(data || []);

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

type BatchTicketRedeemRowsQueryResponse = {
  data: ReturnType<typeof normalizeBatchTicketRedeemRows>;
  meta: MetaResponse;
};

type BatchTicketRedeemRowsQueryParams = {
  page: number;
  basketAdvisorId: string;
  searchTerm: string;
};

export const batchTicketRedeemRowsQuery = selectorFamily<
  BatchTicketRedeemRowsQueryResponse,
  BatchTicketRedeemRowsQueryParams
>({
  key: 'batchTicketRedeemRowsQuery',
  get: ({ basketAdvisorId, searchTerm, page }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const isFiltering = searchTerm.length > 0;

        const { data, meta } = await Service.Basket.V1.getCustomersOffersFileRowsBasketAdvisor({
          basketAdvisorOrderId: basketAdvisorId,
          page,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          screenName: 'boleta-lote',
          ...(isFiltering && { searchTerm }),
        });

        const normalizedData = normalizeBatchTicketRedeemRows(data || []);

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export type BatchTicketRedeemSummaryQueryResponse = {
  total: number;
  processed: number;
  sent: number;
  error: number;
  valid: number;
};

type BatchTicketRedeemSummaryQueryParams = { basketAdvisorId: string };

export const batchTicketRedeemSummaryQuery = selectorFamily<
  BatchTicketRedeemSummaryQueryResponse,
  BatchTicketRedeemSummaryQueryParams
>({
  key: 'batchTicketRedeemSummaryQuery',
  get: ({ basketAdvisorId }) => {
    return async () => {
      const { data } = await Service.Basket.V1.getCustomersByOffersBasketAdvisorConsolidated({
        basketAdvisorId,
        screenName: 'boleta-lote',
      });

      return {
        total: data?.totalBasketOffer || 0,
        valid: data?.validateTotal || 0,
        error: data?.errorTotal || 0,
        sent: data?.sendTotal || 0,
        processed: data?.validateTotal || 0,
        processedError: data?.errorTotal || 0,
      };
    };
  },
});

export type BatchTicketRedeemRowsSummaryQueryResponse = {
  total: number;
  error: number;
  valid: number;
};

export const batchTicketRedeemRowsSummaryQuery = selectorFamily<
  BatchTicketRedeemRowsSummaryQueryResponse,
  BatchTicketRedeemSummaryQueryParams
>({
  key: 'batchTicketRedeemRowsSummaryQuery',
  get: ({ basketAdvisorId }) => {
    return async () => {
      const { data } =
        await Service.Basket.V1.getCustomersByOffersFileRowsBasketAdvisorConsolidated({
          basketAdvisorId,
          screenName: 'boleta-lote',
        });

      return {
        total: data?.totalRows || 0,
        valid: data?.totalValidRows || 0,
        error: data?.totalErrorRows || 0,
      };
    };
  },
});
